import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronDown, Heart } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { setSearchQuery, setSelectedSpecialty, setZipCode } from '../../redux/therapist/filterSlice';

interface FilterFormData {
  specialty: string;
  location: string;
  insurance: string;
  sessionRate: string;
  sessionType: string;
  ageRanges: string[];
  therapyModalities: string[];
  languages: string[];
  faithSpirituality: string;
  paymentMethods: string[];
  rememberMe: boolean;
}

export const TherapistFilter: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);
  const [formData, setFormData] = useState<FilterFormData>({
    specialty: '',
    location: '',
    insurance: '',
    sessionRate: '',
    sessionType: '',
    ageRanges: [],
    therapyModalities: [],
    languages: [],
    faithSpirituality: '',
    paymentMethods: [],
    rememberMe: false,
  });

  const specialties = [
    "Anxiety", "Depression", "Trauma & PTSD", "ADHD", "Relationship Issues",
    "Grief & Loss", "Eating Disorders", "Bipolar Disorder", "OCD", "Substance Abuse",
    "Life Transitions", "Anger Management", "Self Esteem", "Career Counseling",
    "Family Conflict", "Stress Management", "LGBTQ+ Issues", "Chronic Pain",
    "Sleep Disorders", "Autism Spectrum", "Behavioral Issues"
  ];

  const insuranceOptions = [
    "Aetna", "Anthem", "Blue Cross Blue Shield", "Cigna", "Humana",
    "Kaiser Permanente", "Medicaid", "Medicare", "UnitedHealthcare",
    "Tricare", "Out-of-pocket", "Sliding scale"
  ];

  const sessionTypes = [
    "Individual therapy", "Couples therapy", "Family therapy", "Group therapy"
  ];

  const ageRangeOptions = [
    "Children (4-10)", "Preteens (11-13)", "Adolescents (14-17)",
    "Adults (18-64)", "Seniors (65+)"
  ];

  const therapyModalityOptions = [
    "Cognitive Behavioral (CBT)", "Dialectical Behavior (DBT)", "EMDR",
    "Psychodynamic", "Mindfulness-Based", "Acceptance and Commitment (ACT)",
    "Solution-Focused", "Humanistic"
  ];

  const languageOptions = [
    "English", "Spanish", "French", "German", "Mandarin", "Arabic"
  ];

  const faithOptions = [
    "Christian", "Jewish", "Muslim", "Buddhist", "Hindu",
    "Spiritual but not religious", "Non-religious"
  ];

  const paymentMethodOptions = [
    "Insurance", "Credit/Debit Card", "HSA/FSA", "Cash",
    "Check", "Sliding scale", "Payment plans"
  ];

  const handleInputChange = useCallback((field: keyof FilterFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleArrayChange = useCallback((field: keyof FilterFormData, value: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...(prev[field] as string[]), value]
        : (prev[field] as string[]).filter(item => item !== value)
    }));
  }, []);

  const isFormValid = formData.specialty && formData.location && formData.insurance;

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isFormValid) return;

    // Update Redux store with basic filters
    dispatch(setSearchQuery(''));
    dispatch(setSelectedSpecialty(formData.specialty));
    dispatch(setZipCode(formData.location));

    // Create URL params for navigation
    const searchParams = new URLSearchParams();
    if (formData.specialty) searchParams.set('specialty', formData.specialty);
    if (formData.location) searchParams.set('location', formData.location);
    if (formData.insurance) searchParams.set('insurance', formData.insurance);
    if (formData.sessionRate) searchParams.set('sessionRate', formData.sessionRate);
    if (formData.sessionType) searchParams.set('sessionType', formData.sessionType);
    if (formData.ageRanges.length) searchParams.set('ageRanges', formData.ageRanges.join(','));
    if (formData.therapyModalities.length) searchParams.set('modalities', formData.therapyModalities.join(','));
    if (formData.languages.length) searchParams.set('languages', formData.languages.join(','));
    if (formData.faithSpirituality) searchParams.set('faith', formData.faithSpirituality);
    if (formData.paymentMethods.length) searchParams.set('payment', formData.paymentMethods.join(','));

    navigate(`/find-therapist?${searchParams.toString()}`);
  }, [formData, isFormValid, dispatch, navigate]);

  return (
    <div className="max-w-4xl mx-auto mb-8">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
        <div className="text-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Find Your Perfect Therapist</h3>
          <p className="text-gray-600">Start with these required fields to get personalized matches</p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          {/* Required Fields */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Specialty */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Specialty <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  className="appearance-none w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 pr-10 text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                  value={formData.specialty}
                  onChange={(e) => handleInputChange('specialty', e.target.value)}
                >
                  <option value="">What brings you to therapy?</option>
                  {specialties.map(specialty => (
                    <option key={specialty} value={specialty}>{specialty}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-3 flex items-center text-gray-400">
                  <Search className="w-4 h-4" />
                </div>
                <input
                  type="text"
                  placeholder="City, State or ZIP code"
                  className="w-full bg-gray-50 border border-gray-300 rounded-xl pl-10 pr-4 py-3 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                />
              </div>
            </div>

            {/* Insurance */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                How are you planning on paying <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  className="appearance-none w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 pr-10 text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                  value={formData.insurance}
                  onChange={(e) => handleInputChange('insurance', e.target.value)}
                >
                  <option value="">Select insurance or other</option>
                  {insuranceOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Additional Filters Toggle */}
          <div className="border-t border-gray-200 pt-6">
            <button
              type="button"
              onClick={() => setShowAdditionalFilters(!showAdditionalFilters)}
              className="flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium transition-colors mb-4"
            >
              <ChevronDown 
                className={`w-4 h-4 transition-transform ${showAdditionalFilters ? 'rotate-180' : ''}`}
              />
              Additional Filters (Optional)
            </button>

            {/* Additional Filters */}
            {showAdditionalFilters && (
              <div className="space-y-6 bg-gray-50 rounded-xl p-6">
                {/* Session Rate and Type */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Session Rate</label>
                    <input
                      type="number"
                      placeholder="Maximum rate per session ($)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      value={formData.sessionRate}
                      onChange={(e) => handleInputChange('sessionRate', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Session Type</label>
                    <select
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      value={formData.sessionType}
                      onChange={(e) => handleInputChange('sessionType', e.target.value)}
                    >
                      <option value="">Any session type</option>
                      {sessionTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Age Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Age Range Treated</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    {ageRangeOptions.map(range => (
                      <label key={range} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          checked={formData.ageRanges.includes(range)}
                          onChange={(e) => handleArrayChange('ageRanges', range, e.target.checked)}
                        />
                        <span className="text-sm text-gray-700">{range}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Therapy Modalities */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Therapy Modalities</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {therapyModalityOptions.map(modality => (
                      <label key={modality} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          checked={formData.therapyModalities.includes(modality)}
                          onChange={(e) => handleArrayChange('therapyModalities', modality, e.target.checked)}
                        />
                        <span className="text-sm text-gray-700">{modality}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Languages and Faith */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Languages Spoken</label>
                    <div className="grid grid-cols-2 gap-3">
                      {languageOptions.map(language => (
                        <label key={language} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                            checked={formData.languages.includes(language)}
                            onChange={(e) => handleArrayChange('languages', language, e.target.checked)}
                          />
                          <span className="text-sm text-gray-700">{language}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Faith & Spirituality</label>
                    <select
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      value={formData.faithSpirituality}
                      onChange={(e) => handleInputChange('faithSpirituality', e.target.value)}
                    >
                      <option value="">No preference</option>
                      {faithOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Payment Methods */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Accepted Payment Methods</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {paymentMethodOptions.map(method => (
                      <label key={method} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          checked={formData.paymentMethods.includes(method)}
                          onChange={(e) => handleArrayChange('paymentMethods', method, e.target.checked)}
                        />
                        <span className="text-sm text-gray-700">{method}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="mt-6">
            <button
              type="submit"
              disabled={!isFormValid}
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              <Search className="w-5 h-5" />
              Find Therapists
              {!isFormValid && (
                <span className="text-sm opacity-75">(Complete required fields)</span>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Footer Text */}
      <div className="flex items-center justify-center mt-4 text-sm text-gray-600">
        <Heart className="w-4 h-4 mr-2" />
        <span>Personalized therapist matching</span>
        <span className="mx-2">•</span>
        <span className="text-gray-400">Free service</span>
      </div>
    </div>
  );
}; 